/* eslint-disable default-case */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-params */
import { Box3, Vector3, type Object3D } from 'three';

import { RENDERING_CAPTURE_HEIGHT, RENDERING_CAPTURE_WIDTH } from '../../../../capture/rendering-capture.constants';
import { SCENE_CATEGORY_TYPES, type SceneCategory } from '../../../interaction';
import type { ThreeSceneEngine } from '../../../engine';

// Stable per-engine identity: Render3DKitchen builds a fresh adapter object per registration, so
// object identity cannot tell "same engine re-registering" from "a second engine stealing the slot".
const engineIds = new WeakMap<ThreeSceneEngine, string>();
let nextEngineId = 0;

const getEngineId = (engine: ThreeSceneEngine): string => {
	const existing = engineIds.get(engine);
	if (existing) return existing;
	nextEngineId += 1;
	const id = `engine-${nextEngineId}`;
	engineIds.set(engine, id);
	return id;
};

const islandGroups = (engine: ThreeSceneEngine): Array<Object3D> => {
	const objectHolder = engine.sceneManager?.objectHolder;
	if (!objectHolder) return [];
	return Array.from(objectHolder.children).filter((child) => child.userData?.type === 'island');
};

/** Frame a box. One way in for every zoom below, so they all pad and animate alike. */
const fitCameraToBox = (engine: ThreeSceneEngine, box: Box3, paddingFactor?: number): boolean => {
	const cameraManager = engine.cameraManager;
	if (!cameraManager || box.isEmpty()) return false;

	cameraManager.zoomToFit(box.min, box.max, true, paddingFactor);
	engine.requestRender();
	return true;
};

/** What the kitchen holds of the given `userData.type`s, as one box in world space. */
const boxOfTypes = (engine: ThreeSceneEngine, types: Set<string>): Box3 => {
	const box = new Box3();
	for (const island of islandGroups(engine)) {
		island.traverse((child) => {
			if (types.has(child.userData?.type as string)) box.expandByObject(child);
		});
	}
	return box;
};

/**
 * Create an adapter that maps ThreeSceneEngine to the legacy UseThreeKitchenReturn interface.
 * This allows the controller to work with both the old and new implementations.
 */
export const createThreeInstanceAdapter = (engine: ThreeSceneEngine): any => ({
	instanceId: getEngineId(engine),
	isActive: () => engine.isActive,
	requestRender: () => engine.requestRender(),
	getCameraControls: () => engine.cameraManager?.controls ?? null,
	setZoom: (zoom: number) => engine.setZoom(zoom),
	setOptimalCameraDistance: (width: number, depth: number, height: number, zoom: number) => {
		engine.cameraManager?.setOptimalDistance(
			{ width, depth, height },
			zoom,
			{ x: 0, y: 0 }
		);
	},
	rotateCamera: async (angle: number, elevation?: number, duration?: number) => {
		await engine.rotateCamera(angle, elevation, duration);
	},
	zoomToAll: async (): Promise<void> => {
		const box = new Box3();
		for (const island of islandGroups(engine)) box.expandByObject(island);
		fitCameraToBox(engine, box);
	},
	zoomToIsland: async (islandId: string): Promise<void> => {
		const island = islandGroups(engine).find((child) => String(child.userData?.id) === String(islandId));
		if (!island) return;
		fitCameraToBox(engine, new Box3().setFromObject(island));
	},
	// Frame one legend category — the vents, whose only presence in the scene is their cutout proxies.
	zoomToCategory: async (category: string): Promise<boolean> => {
		const types = SCENE_CATEGORY_TYPES[category as SceneCategory];
		if (!types?.length) return false;
		// A vent is a few inches of slot: fit it loosely, or the camera ends up inside the island.
		return fitCameraToBox(engine, boxOfTypes(engine, new Set(types)), 1.8);
	},
	getImageRender: (pixelRatio: number, callback: (data: string) => void) => {
		const data = engine.getImageRender({ pixelRatio, width: RENDERING_CAPTURE_WIDTH, height: RENDERING_CAPTURE_HEIGHT });
		if (data) callback(data);
	},
	getCanvasElement: () => engine.sceneManager?.renderer?.domElement ?? null,
	exportModel: async (options?: any) => {
		return engine.exportModel(options);
	},
	activateAR: async () => {
		await engine.activateAR();
	},
	highlightObject: (objectId: string, addHighlight?: boolean) => {
		engine.highlightObject(objectId, addHighlight);
	},
	clearHighlight: () => {
		engine.clearHighlight();
	},
	setDebugMode: (enabled: boolean, options?: { cutouts?: boolean; lights?: boolean; camera?: boolean; bounds?: boolean }) => {
		engine.setDebugMode(enabled, options);
	},
	setHoverTrackingEnabled: (enabled: boolean) => {
		engine.setHoverTrackingEnabled(enabled);
	},
	setCategoryHighlight: (category: string | null) => {
		engine.setCategoryHighlight(category as never);
	},
	setDebugCutouts: (enabled: boolean) => {
		engine.setDebugCutouts(enabled);
	},
	setDebugLights: (enabled: boolean) => {
		engine.setDebugLights(enabled);
	},
	setDebugCamera: (enabled: boolean) => {
		engine.setDebugCamera(enabled);
	},
	setRenderQuality: (quality: number) => {
		engine.setQuality(quality as any);
	},
	setTextureQuality: async (quality: number | null): Promise<void> => {
		await engine.setTextureQuality(quality);
	},
	getRendererInfo: () => {
		const renderer = engine.sceneManager?.renderer;
		if (!renderer) return null;
		const info = renderer.info;
		return {
			memory: { textures: info.memory.textures, geometries: info.memory.geometries },
			render: { calls: info.render.calls, triangles: info.render.triangles },
			programsCount: info.programs?.length ?? 0,
			pixelRatio: renderer.getPixelRatio(),
		};
	},
	getIslands: () => islandGroups(engine),
	showControls: (_show: boolean) => {
		// Brightness panel is now a React overlay — no engine-side action needed
	},
	setBrightness: (value: number) => {
		engine.setBrightness(value);
	},
	restoreBrightness: () => {
		engine.restoreBrightness();
	},
	changeTexture: (type: string, alias: string) => {
		switch (type) {
			case 'Surround':
				engine.changeSurroundTexture(alias);
				break;
			case 'Counter':
			case 'Trim':
				engine.changeCountertopTexture(alias);
				break;
			case 'Sky':
				engine.changeSkyTexture(alias);
				break;
			case 'Floor':
				engine.changeFloorTexture(alias);
				break;
		}
	},
	setCustomSkyTexture: async (dataUrl: string) => {
		await engine.setCustomSkyTexture(dataUrl);
	},
	// Geographic lighting methods
	enableGeoLighting: (location: { latitude: number; longitude: number }) => {
		engine.enableGeoLighting(location);
	},
	disableGeoLighting: () => {
		engine.disableGeoLighting();
	},
	setGeoTime: (time: Date | string) => {
		engine.setGeoTime(time as any);
	},
	setWeatherPreset: (preset: string) => {
		engine.setWeatherPreset(preset as any);
	},
	getGeoLightingState: () => {
		const state = engine.geoLightingState;
		if (!state) return null;
		return {
			enabled: state.config.enabled,
			location: state.config.location,
			time: typeof state.config.time === 'string' ? state.config.time : state.config.time.toISOString(),
			weather: state.config.weather
		};
	},
	// Floor and sky texture methods (also exposed directly)
	changeFloorTexture: (textureKey: string) => {
		engine.changeFloorTexture(textureKey);
	},
	changeSkyTexture: (textureKey: string) => {
		engine.changeSkyTexture(textureKey);
	},
	clearSkyTexture: () => {
		engine.clearSkyTexture();
	},
	// Incremental scene update method
	updateScene: async (buildInput: any) => {
		await engine.updateScene([{ builder: 'kitchen', data: buildInput }]);
	},
	// Targeted scene changes (tier-based updates without full rebuild)
	applyTargetedChanges: async (changes: any) => {
		await engine.applyTargetedChanges(changes);
	},
	// Rotation gizmo control
	activateRotationGizmo: (objectId: string) => {
		engine.activateRotationGizmo(objectId);
	},
	deactivateRotationGizmo: () => {
		engine.deactivateRotationGizmo();
	},
	// Appliance drag-and-drop (press-and-hold)
	setApplianceDragProvider: (provider: any) => {
		engine.setApplianceDragProvider(provider);
	},
	setApplianceDragEnabled: (enabled: boolean, options?: any) => {
		engine.setApplianceDragEnabled(enabled, options);
	},
	cancelApplianceDrag: () => {
		engine.cancelApplianceDrag();
	},
	isApplianceDragActive: () => engine.isApplianceDragActive,
	// Layer visibility toggle (no rebuild needed)
	setLayerVisibility: (type: string, visible: boolean) => {
		engine.setLayerVisibility(type, visible);
	},
	// Look at a specific object by ID without moving the camera
	lookAtObject: async (objectId: string, duration = 1000): Promise<boolean> => {
		const controls = engine.cameraManager?.controls;
		const objectHolder = engine.sceneManager?.objectHolder;
		const scene = engine.sceneManager?.scene;
		if (!controls) return false;

		// Find the object by ID — search objectHolder first, then full scene
		let foundObject: any = null;
		const targetIdStr = String(objectId);

		const searchDown = (root: any): void => {
			if (!root) return;
			root.traverse((child: any) => {
				if (!foundObject && child.userData?.id != null) {
					if (String(child.userData.id) === targetIdStr) {
						foundObject = child;
					}
				}
			});
		};

		if (objectHolder) {
			searchDown(objectHolder);
		}
		if (!foundObject && scene) {
			searchDown(scene);
		}
		if (!foundObject) {
			return false;
		}

		// Compute the center of the object's bounding box
		const { Box3, Vector3 } = await import('three');
		const box = new Box3().setFromObject(foundObject);
		if (box.isEmpty()) {
			return false;
		}
		const center = new Vector3();
		box.getCenter(center);

		// Smoothly rotate the camera target to the object center
		// setTarget changes only where the camera looks, not its position
		const enableTransition = duration > 0;
		await controls.setTarget(center.x, center.y, center.z, enableTransition);

		return true;
	},
	// Reset the view: recalculate bounds, camera position, auto-zoom, presentation point
	resetView: async (kitchenDebugData?: any): Promise<void> => {
		await engine.resetView(kitchenDebugData);
	},
	// Wait for scene readiness (all manifested appliances present, textures applied)
	waitForSceneReady: async (options?: { timeoutMs?: number }) => {
		return engine.waitForSceneReady(options);
	},
	// Slide all appliance meshes by a world-space delta without rebuilding the scene
	slideAppliances: (delta: { x: number; y: number; z: number }, duration?: number) => {
		const objectHolder = engine.sceneManager?.objectHolder;
		const animationManager = engine.animationManager;
		if (!objectHolder) return;

		const appliances: Array<import('three').Object3D> = [];
		objectHolder.traverse((child) => {
			if (child.userData?.type === 'appliance') appliances.push(child);
		});

		if (appliances.length === 0) return;

		const dur = duration ?? 150;
		for (const mesh of appliances) {
			const start = mesh.position.clone();
			const end = new Vector3(start.x + delta.x, start.y + delta.y, start.z + delta.z);

			if (animationManager && dur > 0) {
				animationManager.animatePosition(
					`slide-appliance-${mesh.uuid}`,
					start,
					end,
					dur,
					(pos) => { mesh.position.copy(pos); }
				);
			} else {
				mesh.position.copy(end);
			}
		}
	}
});
